import { createContext, useCallback, useContext, useRef, useState, useEffect, type ReactNode } from 'react'

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:5000'

// Module-level: survives React StrictMode unmount/remount so both effects share
// the same in-flight promise and only one refresh request hits the server.
let moduleRefreshPromise: Promise<string | null> | null = null

interface AuthUser { id: string; role: string; tenantId: string; name: string; email: string }
interface TokenState { accessToken: string; expiry: number }
interface OtpChallenge { requiresOtp: true; challengeId: string; emailHint: string }
type LoginResult = void | OtpChallenge

interface AuthCtx {
  user: AuthUser | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<LoginResult>
  verifyOtp: (challengeId: string, code: string) => Promise<void>
  register: (workshopName: string, ownerName: string, email: string, password: string, phone?: string) => Promise<void>
  logout: () => Promise<void>
  getToken: () => Promise<string | null>
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const tokenRef = useRef<TokenState | null>(null)

  // The refresh token is stored in an httpOnly cookie (set by the server) — never
  // accessed by JS. applyTokens only needs the access token.
  const applyTokens = useCallback((accessToken: string) => {
    const parsed = parseJwt(accessToken)
    tokenRef.current = { accessToken, expiry: parsed.exp * 1000 }
    setUser({ id: parsed.sub, role: parsed.role, tenantId: parsed.tenantId, name: parsed.name ?? '', email: parsed.email ?? '' })
  }, [])

  const doRefresh = useCallback(async (): Promise<string | null> => {
    // If a prior refresh already completed and set a valid token, reuse it — avoids
    // hitting the rotating-token endpoint twice when React StrictMode double-fires effects.
    const existing = tokenRef.current
    if (existing && Date.now() < existing.expiry - 30_000) return existing.accessToken

    if (moduleRefreshPromise) return moduleRefreshPromise
    const promise = (async () => {
      try {
        // No body — the httpOnly cookie is sent automatically by the browser.
        const res = await fetch(`${BASE}/v1/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        })
        if (!res.ok) {
          // Only clear the in-memory session on 401. Server errors or transient
          // network failures shouldn't log the user out.
          if (res.status === 401 || res.status === 423) {
            tokenRef.current = null
            setUser(null)
          }
          return null
        }
        const { accessToken } = await res.json()
        applyTokens(accessToken)
        return accessToken as string
      } catch {
        return null
      } finally {
        moduleRefreshPromise = null
      }
    })()
    moduleRefreshPromise = promise
    return promise
  }, [applyTokens])

  // Restore session on mount
  useEffect(() => {
    doRefresh().finally(() => setIsLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getToken = useCallback(async (): Promise<string | null> => {
    const t = tokenRef.current
    if (t && Date.now() < t.expiry - 30_000) return t.accessToken
    return doRefresh()
  }, [doRefresh])

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${BASE}/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data?.error?.message ?? 'Login failed')
    }
    const data = await res.json()
    if (data.requiresOtp) return data as OtpChallenge
    applyTokens(data.accessToken)
  }, [applyTokens])

  const verifyOtp = useCallback(async (challengeId: string, code: string) => {
    const res = await fetch(`${BASE}/v1/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeId, code }),
      credentials: 'include',
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data?.error?.message ?? 'OTP verification failed')
    }
    const { accessToken } = await res.json()
    applyTokens(accessToken)
  }, [applyTokens])

  const register = useCallback(async (
    workshopName: string, ownerName: string, email: string, password: string, phone?: string,
  ) => {
    const res = await fetch(`${BASE}/v1/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workshopName, ownerName, email, password, phone }),
      credentials: 'include',
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data?.error?.message ?? 'Registration failed')
    }
    const { accessToken } = await res.json()
    applyTokens(accessToken)
  }, [applyTokens])

  const logout = useCallback(async () => {
    const t = tokenRef.current
    await fetch(`${BASE}/v1/auth/logout`, {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t.accessToken}` } : {},
      credentials: 'include',
    }).catch(() => {})
    tokenRef.current = null
    setUser(null)
  }, [])

  return (
    <Ctx.Provider value={{ user, isLoading, login, verifyOtp, register, logout, getToken }}>
      {children}
    </Ctx.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

function parseJwt(token: string): Record<string, string & number> {
  return JSON.parse(atob(token.split('.')[1]))
}
